import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Editor } from "primereact/editor";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useSmartBack } from "hooks/useSmartBack";
import AITranslateButton from "components/common/AITranslateButton";
import { useDispatch, useSelector } from "react-redux";
import {
  getPageContentByType,
  updatePageContentByType,
} from "store/slices/pageContentSlice";
import { handleSubmitForm } from "helpers/apiUtils";
import PageHeader from "components/common/PageHeader";

const PAGE_CONTENT_META = {
  TermsAndConditions: {
    title: "Terms & Conditions",
    subTitle: "Create and update the terms and conditions for app users",
  },
  PrivacyPolicy: {
    title: "Privacy Policy",
    subTitle: "Create and update the privacy policy for app users",
  },
  FAQ: {
    title: "FAQ",
    subTitle: "Create and update the FAQ for app users",
  },
};

const PageContentEditor = ({ defaultType }) => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, isLoading } = useSelector((state) => state.PageContent);

  const CONTENT_TYPE = location.state?.contentType || defaultType;
  const meta = PAGE_CONTENT_META[CONTENT_TYPE] || {};

  // ✅ Key trick: forces Editor to remount once real data is available
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (CONTENT_TYPE) {
      setIsDataLoaded(false); // reset on type change
      dispatch(getPageContentByType({ type: CONTENT_TYPE }));
    }
  }, [CONTENT_TYPE]);

  const [initialValues, setInitialValues] = useState({
    descriptionEn: "",
    descriptionAr: "",
  });

  useEffect(() => {
    if (data) {
      setInitialValues({
        descriptionEn: data.description || "",
        descriptionAr: data.descriptionAr || "",
      });
      setIsDataLoaded(true); // ✅ triggers Editor remount with populated values
    }
  }, [data]);

  const validationSchema = Yup.object({
    descriptionEn: Yup.string().required(t("English description is required")),
    descriptionAr: Yup.string().required(t("Arabic description is required")),
  });

  const handleSubmit = (values) => {
    const data = {
      type: CONTENT_TYPE,
      description: values.descriptionEn,
      descriptionAr: values.descriptionAr,
    };
    handleSubmitForm({
      dispatch,
      action: updatePageContentByType,
      payload: data,
      onSuccess: () => navigate(-1),
    });
  };

  return (
    <div className="page-content term_condition_page">
      <PageHeader title={t(meta.title)} subTitle={t(meta.subTitle)} />

      <div className="common_card">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form>
              <Row className="g-4">
                {/* English Description */}
                <Col lg={6}>
                  <div className="form_field position-relative">
                    <AITranslateButton
                      sourceField="descriptionEn"
                      targetField="descriptionAr"
                      translateTo="ar"
                    />
                    <label>{t("(English) Description")}</label>
                    <Editor
                      key={`en-${isDataLoaded}`} // ✅
                      value={values.descriptionEn}
                      onTextChange={(e) =>
                        setFieldValue("descriptionEn", e.htmlValue)
                      }
                      style={{ height: "400px" }}
                    />
                    {errors.descriptionEn && touched.descriptionEn && (
                      <div className="text-danger">{errors.descriptionEn}</div>
                    )}
                  </div>
                </Col>

                {/* Arabic Description */}
                <Col lg={6}>
                  <div className="form_field position-relative">
                    <AITranslateButton
                      sourceField="descriptionAr"
                      targetField="descriptionEn"
                      translateTo="en"
                    />
                    <label>{t("(Arabic) Description")}</label>
                    <Editor
                      key={`ar-${isDataLoaded}`} // ✅
                      value={values.descriptionAr}
                      onTextChange={(e) =>
                        setFieldValue("descriptionAr", e.htmlValue)
                      }
                      style={{ height: "400px" }}
                      className="rtl_editor"
                    />
                    {errors.descriptionAr && touched.descriptionAr && (
                      <div className="text-danger">{errors.descriptionAr}</div>
                    )}
                  </div>
                </Col>

                {/* Buttons */}
                <Col lg={12} className="mt-3">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="main_btn outline"
                      onClick={goBack}
                      disabled={isLoading}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="main_btn"
                      disabled={isLoading}
                    >
                      {isLoading ? t("Saving...") : t("Save")}
                    </button>
                  </div>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PageContentEditor;
